<template>
  <div class="products-table-outer">
    <div class="blank"></div>
    <div class="table-wrapper">
      <div class="table-wrapper-inner">
        <span class="title">Specialty Brands Partnerships</span>
        <div class="table" :class="{ glow: props.glow }">
          <div class="table-body">
            <div
              class="partnership"
              v-for="(partnership, rowIndex) in partnerships"
              :key="rowIndex"
            >
              <div class="partnership-items">
                <span
                  class="partnership-item"
                  v-for="(partnershipItem, itemIdex) in partnership.partnershipItems"
                  :key="itemIdex"
                  >{{ partnershipItem }}</span
                >
              </div>
              <div class="partnership-logo">
                <component
                  :is="partnership.logo"
                  v-bind="partnership.logoProps"
                />
              </div>
            </div>
            <div class="bottom-text">
              <PlusOutlinedIcon/>
              and more
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PlusOutlinedIcon } from '@/components/icons'
import AdvanzLogo from '@/components/icons/AdvanzLogo.vue'
import AstrazenecaLogo from '@/components/icons/AstrazenecaLogo.vue'
import GskLogo from '@/components/icons/GskLogo.vue'
// import LeoLogo from '@/components/icons/LeoLogo.vue'
import MenariniLogo from '@/components/icons/MenariniLogo.vue'
import MsdLogo from '@/components/icons/MsdLogo.vue'
import MundiPharmaLogo from '@/components/icons/MundiPharmaLogo.vue'
import NovartisLogo from '@/components/icons/NovartisLogo.vue'
import ServierLogo from '@/components/icons/ServierLogo.vue'
import TakedaLogo from '@/components/icons/TakedaLogo.vue'
import { useThemeStore } from '@/stores'
import { computed } from 'vue'

interface IProps {
  glow?: boolean
}

const props = defineProps<IProps>()

const themeStore = useThemeStore()
const { themeColors } = themeStore

const partnerships = computed(() => [
  {
    partnerName: 'AstraZenica',
    logo: AstrazenecaLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: ['Symbicort/Rilast Turbuhaler']
  },
  {
    partnerName: 'GSK',
    logo: GskLogo,
    logoProps: { height: '1.875rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: [
      'Seretide/Pavtide MDI',
      'Seretide/Pavtide Accuhaler',
      'Flixotide/Axotide MDI',
      'Duodart/Doubluts'
    ]
  },
  {
    partnerName: 'Servier',
    logo: ServierLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: [
      'Coversyl/Prexum',
      'Coveram/Reaptan',
      'Domion/Valdoxan',
      'Diamicron/Ardix Gliclazide'
    ]
  },
  {
    partnerName: 'Novartis',
    logo: NovartisLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: ['Gilenya']
  },
  {
    partnerName: 'Menarini',
    logo: MenariniLogo,
    logoProps: {
      height: '3.75rem',
      width: '9.375rem',
      fill: themeColors.colorBackgroundBright,
      fill2: themeColors.colorText
    },
    partnershipItems: ['Crestor', 'Nebilet']
  },
  {
    partnerName: 'Takeda',
    logo: TakedaLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: ['Mezavant/Mesalazine 1.2 TAKEDA']
  },
  {
    partnerName: 'MSD',
    logo: MsdLogo,
    logoProps: { height: '3.4375rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: ['Januvia/Xelevia', 'Janumet/Velmetia', 'Janumet XR']
  },
  // {
  //   partnerName: 'Leo',
  //   logo: LeoLogo,
  //   logoProps: { height: 60, width: 150, fill: themeColors.colorBackgroundBright },
  //   partnershipItems: ['Advantan/Supriad']
  // },
  {
    partnerName: 'Advanz',
    logo: AdvanzLogo,
    logoProps: { height: '2.8125rem', width: '9.375rem', fill: themeColors.colorBackgroundBright },
    partnershipItems: ['Mytolac']
  },
  {
    partnerName: 'Mundi Pharma',
    logo: MundiPharmaLogo,
    logoProps: {
      height: '3.75rem',
      width: '9.375rem',
      fill: themeColors.colorBackgroundBright,
      fill2: themeColors.colorText
    },
    partnershipItems: ['Norspan/Bupredermal', 'Oxycontin', 'Targin']
  }
])
</script>

<style scoped>
.products-table-outer {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5%;
}

.blank {
  height: 3dvh;
}

.table-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-secondary);
  color: var(--color-background-bright);
  transition:
    background-color 0.5s ease,
    flex 0.5s ease;
  width: 100%;
  height: 100%;
  border-radius: 6.25rem;
  padding-block: 5%;
  min-width: 37.5rem;
  min-height: 52.25rem;
}

.table-wrapper-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  gap: 1.25rem;
}

.title {
  font-size: var(--font-size-h2);
  font-weight: 600;
}

.table {
  width: 100%;
  position: relative;
}

.table-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  height: 100%;
  gap: 0.625rem;
}

.table .table-body .partnership {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table .table-body .partnership:nth-child(even) {
  background-color: var(--color-background-bright-5);
}

.table .table-body .partnership .partnership-items {
  width: 60%;
  display: flex;
  align-items: center;
  flex-direction: column;
  line-height: var(--font-size-table);
  font-size: var(--font-size-table);
}

.table .table-body .partnership .partnership-logo {
  width: 40%;
  min-height: 4.0625rem;
  display: flex;
  align-items: center;
}

.table .table-body .bottom-text {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  font-size: var(--font-size-caption);
}

.table.glow:before {
  content: '';
  width: 50%;
  aspect-ratio: 1;
  background-color: #2aace2;
  position: absolute;
  top: -18.5%;
  left: -7.5%;
  z-index: -1;
  border-radius: 60%;
  filter: blur(2.5rem);
  opacity: 0.5;
}

.table.glow:after {
  content: '';
  width: 50%;
  aspect-ratio: 1;
  background-color: #2aace2;
  position: absolute;
  bottom: -14.5%;
  right: -7.5%;
  z-index: -1;
  border-radius: 60%;
  filter: blur(2.5rem);
  opacity: 0.5;
}
</style>
